import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Gift, ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import Navigation from "@/components/Navigation";

// Create stripe promise only once, outside component to prevent re-initialization
// Debug: Log the Stripe public key being used
if (import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  console.log("Stripe public key found:", import.meta.env.VITE_STRIPE_PUBLIC_KEY.substring(0, 10) + "...");
} else {
  console.error("VITE_STRIPE_PUBLIC_KEY is not set in environment variables!");
}

const stripePromise = import.meta.env.VITE_STRIPE_PUBLIC_KEY 
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY)
  : null;

const AMOUNT_PRESETS = [25, 50, 100, 150];

const formSchema = z.object({
  recipientEmail: z.string().email("Please enter a valid email address"),
  recipientName: z.string().min(2, "Recipient name must be at least 2 characters"),
  amount: z.number().min(25, "Minimum amount is $25").max(500, "Maximum amount is $500"),
  message: z.string().optional(),
  purchaserEmail: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
});

type FormData = z.infer<typeof formSchema>;

interface GiftCardDesign {
  id: string;
  name: string;
  imageUrl: string;
}

function BuyGiftCardForm() {
  const { toast } = useToast();
  const [selectedDesignIndex, setSelectedDesignIndex] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentStep, setPaymentStep] = useState<"form" | "payment" | "success">("form");
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const { data: designsData, isLoading: isLoadingDesigns } = useQuery<{ success: boolean; designs: GiftCardDesign[] }>({
    queryKey: ["/api/cardivo/gift-cards/designs"],
  });

  const designs = designsData?.designs || [];

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipientEmail: "",
      recipientName: "",
      amount: 25,
      message: "",
      purchaserEmail: "",
    },
  });

  const createCheckoutMutation = useMutation({
    mutationFn: async (data: FormData & { designId: string }) => {
      const response = await apiRequest("POST", "/api/cardivo/gift-cards/checkout", data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create checkout session");
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.clientSecret) {
        // Debug: Check if clientSecret is a PaymentIntent secret
        console.log("Received clientSecret:", data.clientSecret.substring(0, 10) + "...");
        if (!data.clientSecret.startsWith("pi_")) {
          console.error("Invalid clientSecret format - expected pi_ prefix for PaymentIntent");
        }
        setClientSecret(data.clientSecret);
        setPaymentStep("payment");
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    },
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedDesignIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const handleAmountPresetClick = (amount: number) => {
    form.setValue("amount", amount);
    setShowCustomAmount(false);
    setCustomAmount("");
  };

  const handleCustomAmountClick = () => {
    setShowCustomAmount(true);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 25 && numValue <= 500) {
      form.setValue("amount", numValue);
    }
  };

  const onSubmit = (data: FormData) => {
    if (designs.length === 0) {
      toast({
        title: "Error",
        description: "No designs available",
        variant: "destructive",
      });
      return;
    }

    createCheckoutMutation.mutate({
      ...data,
      designId: designs[selectedDesignIndex].id,
    });
  };

  if (paymentStep === "payment" && clientSecret) {
    if (!stripePromise) {
      console.error("Stripe promise is null - check VITE_STRIPE_PUBLIC_KEY");
      return (
        <div className="container mx-auto max-w-2xl py-12 px-4">
          <Card>
            <CardContent className="pt-8 text-center">
              <p className="text-red-500 font-medium">Stripe is not configured</p>
              <p className="text-sm text-muted-foreground mt-2">
                Please ensure VITE_STRIPE_PUBLIC_KEY is set in your environment variables.
              </p>
              <Button
                onClick={() => {
                  setPaymentStep("form");
                  setClientSecret("");
                }}
                className="mt-4"
              >
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Log successful Stripe initialization
    console.log("Initializing Stripe Elements with clientSecret:", clientSecret.substring(0, 20) + "...");

    const appearance = {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#9333ea',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '8px',
      },
    };

    const options = {
      clientSecret,
      appearance,
      loader: 'auto' as const,
    };

    return (
      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          clientSecret={clientSecret}
          onSuccess={() => setPaymentStep("success")}
          onBack={() => {
            setPaymentStep("form");
            setClientSecret("");
          }}
        />
      </Elements>
    );
  }

  if (paymentStep === "success") {
    return (
      <div className="container mx-auto max-w-2xl py-12 px-4">
          <Card className="bg-gradient-to-br from-purple-50 to-teal-50 dark:from-purple-950 dark:to-teal-950">
            <CardContent className="pt-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-green-100 p-4 dark:bg-green-900">
                  <Check className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h2 className="mb-2 text-2xl font-bold" data-testid="text-success-title">Gift Card Sent!</h2>
              <p className="mb-6 text-muted-foreground" data-testid="text-success-message">
                Your gift card has been sent to {form.getValues("recipientEmail")}
              </p>
              <Button
                onClick={() => {
                  setPaymentStep("form");
                  setClientSecret("");
                  form.reset();
                }}
                data-testid="button-buy-another"
              >
                Buy Another Gift Card
              </Button>
            </CardContent>
          </Card>
        </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent" data-testid="text-page-title">
            Gift a SnuggleCrafters Experience
          </h1>
          <p className="text-muted-foreground" data-testid="text-page-subtitle">
            Share the magic of personalized bedtime stories
          </p>
        </div>

      {isLoadingDesigns ? (
        <div className="flex justify-center py-12" data-testid="loading-designs">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="mb-8">
          <h2 className="mb-4 text-xl font-semibold" data-testid="text-design-title">Choose a Design</h2>
          
          <div className="relative">
            <div className="overflow-hidden" ref={emblaRef} data-testid="carousel-designs">
              <div className="flex">
                {designs.map((design, index) => {
                  // Determine the color based on design name
                  const getDesignColor = (name: string) => {
                    if (name.toLowerCase().includes('purple')) return 'ring-purple-600';
                    if (name.toLowerCase().includes('pink')) return 'ring-pink-500';
                    if (name.toLowerCase().includes('blue')) return 'ring-blue-600';
                    return 'ring-purple-600'; // default
                  };
                  
                  const ringColor = getDesignColor(design.name);
                  
                  return (
                    <div key={design.id} className="flex-[0_0_100%] min-w-0 px-2 md:flex-[0_0_50%] lg:flex-[0_0_33.333%]">
                      <Card 
                        className={`overflow-hidden transition-all cursor-pointer hover:scale-105 ${
                          index === selectedDesignIndex 
                            ? `ring-2 ${ringColor} shadow-lg` 
                            : "opacity-70"
                        }`}
                        onClick={() => {
                          setSelectedDesignIndex(index);
                          emblaApi?.scrollTo(index);
                        }}
                        data-testid={`card-design-${index}`}
                      >
                      <div className="relative aspect-[3/2] bg-gradient-to-br from-purple-100 to-teal-100 dark:from-purple-900 dark:to-teal-900">
                        <img
                          src={design.imageUrl}
                          alt={design.name}
                          className="h-full w-full object-cover"
                          data-testid={`img-design-${index}`}
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                          <Gift className="mb-2 h-12 w-12" data-testid={`icon-gift-${index}`} />
                          <div className="text-2xl font-bold" data-testid={`text-amount-${index}`}>
                            ${form.watch("amount")}
                          </div>
                          <div className="mt-2 text-sm" data-testid={`text-recipient-${index}`}>
                            {form.watch("recipientName") || "Recipient Name"}
                          </div>
                          <div className="mt-1 text-xs opacity-75" data-testid={`text-cardnumber-${index}`}>
                            **** **** **** ****
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-3 text-center">
                        <p className="text-sm font-medium" data-testid={`text-designname-${index}`}>{design.name}</p>
                      </CardContent>
                    </Card>
                  </div>
                  );
                })}
              </div>
            </div>
            
            {designs.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={scrollPrev}
                  data-testid="button-carousel-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={scrollNext}
                  data-testid="button-carousel-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          
          <div className="mt-4 flex justify-center gap-2">
            {designs.map((design, index) => {
              // Determine the color based on design name
              const getDotColor = (name: string) => {
                if (name.toLowerCase().includes('purple')) return 'bg-purple-600';
                if (name.toLowerCase().includes('pink')) return 'bg-pink-500';
                if (name.toLowerCase().includes('blue')) return 'bg-blue-600';
                return 'bg-purple-600'; // default
              };
              
              const dotColor = getDotColor(design.name);
              
              return (
                <button
                  key={index}
                  className={`h-2 w-2 rounded-full transition-all ${
                    index === selectedDesignIndex 
                      ? `w-8 ${dotColor}` 
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                  onClick={() => {
                    setSelectedDesignIndex(index);
                    emblaApi?.scrollTo(index);
                  }}
                  data-testid={`button-dot-${index}`}
                />
              );
            })}
          </div>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle data-testid="text-form-title">Gift Card Details</CardTitle>
              <CardDescription data-testid="text-form-description">
                Enter the recipient's information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="recipientEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient Email *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="recipient@example.com" 
                        {...field} 
                        data-testid="input-recipient-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="recipientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Jane Doe" 
                        {...field} 
                        data-testid="input-recipient-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount *</FormLabel>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                        {AMOUNT_PRESETS.map((amount) => (
                          <Button
                            key={amount}
                            type="button"
                            variant={field.value === amount && !showCustomAmount ? "default" : "outline"}
                            onClick={() => handleAmountPresetClick(amount)}
                            data-testid={`button-amount-${amount}`}
                          >
                            ${amount}
                          </Button>
                        ))}
                        <Button
                          type="button"
                          variant={showCustomAmount ? "default" : "outline"}
                          onClick={handleCustomAmountClick}
                          data-testid="button-amount-custom"
                        >
                          Custom
                        </Button>
                      </div>
                      {showCustomAmount && (
                        <Input
                          type="number"
                          min="25"
                          max="500"
                          step="1"
                          placeholder="Enter amount (25-500)"
                          value={customAmount}
                          onChange={(e) => handleCustomAmountChange(e.target.value)}
                          data-testid="input-custom-amount"
                        />
                      )}
                    </div>
                    <FormDescription>
                      Choose between $25 and $500
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Personal Message (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Add a personal message to the gift card..."
                        className="resize-none"
                        {...field}
                        data-testid="textarea-message"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="purchaserEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="your@example.com" 
                        {...field} 
                        data-testid="input-purchaser-email"
                      />
                    </FormControl>
                    <FormDescription>
                      Receive a confirmation of your purchase
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
              disabled={createCheckoutMutation.isPending || designs.length === 0}
              data-testid="button-proceed-payment"
            >
              {createCheckoutMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Payment
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

function PaymentForm({ 
  clientSecret, 
  onSuccess, 
  onBack 
}: { 
  clientSecret: string; 
  onSuccess: () => void; 
  onBack: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  
  useEffect(() => {
    // Validate clientSecret format
    if (!clientSecret) {
      console.error("No clientSecret provided");
      setInitError("Payment session not initialized properly");
      return;
    }
    
    // Check if clientSecret is a valid PaymentIntent secret
    if (!clientSecret.startsWith("pi_") || !clientSecret.includes("_secret_")) {
      console.error("Invalid clientSecret format:", clientSecret.substring(0, 20) + "...");
      setInitError("Invalid payment session format");
      return;
    }
    
    if (!stripe) {
      console.log("Stripe not yet loaded");
      return;
    }
    
    if (!elements) {
      console.log("Elements not yet initialized");
      return;
    }
    
    // Elements are ready, now verify they can render
    const checkElementsReady = async () => {
      try {
        console.log("Stripe and Elements ready, clientSecret valid:", clientSecret.substring(0, 20) + "...");
        
        // Small delay to ensure Elements are fully initialized
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setIsReady(true);
        setInitError(null);
        console.log("Payment form ready for input");
      } catch (error) {
        console.error("Error preparing payment form:", error);
        setInitError("Failed to initialize payment form");
        toast({
          title: "Payment Form Error",
          description: "Unable to load payment form. Please try again.",
          variant: "destructive",
        });
      }
    };
    
    checkElementsReady();
  }, [stripe, elements, clientSecret, toast]);

  const issueGiftCardMutation = useMutation({
    mutationFn: async (paymentIntentId: string) => {
      const response = await apiRequest("POST", "/api/cardivo/gift-cards/issue", { paymentIntentId });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to issue gift card");
      }
      return response.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to issue gift card",
        variant: "destructive",
      });
      setIsProcessing(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/buy-gift-card",
        },
        redirect: "if_required",
      });

      if (error) {
        toast({
          title: "Payment Failed",
          description: error.message || "An error occurred during payment",
          variant: "destructive",
        });
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        issueGiftCardMutation.mutate(paymentIntent.id);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle data-testid="text-payment-title">Complete Your Purchase</CardTitle>
          <CardDescription data-testid="text-payment-description">
            Enter your payment information to send the gift card
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {initError ? (
              <div className="rounded-lg bg-red-50 p-4 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                <p className="font-medium">Payment Initialization Error</p>
                <p className="text-sm mt-1">{initError}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
              </div>
            ) : !isReady ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p className="text-sm text-muted-foreground">Loading payment form...</p>
              </div>
            ) : (
              <div>
                <PaymentElement 
                  options={{
                    layout: 'tabs',
                    defaultValues: {
                      billingDetails: {
                        email: '',
                        name: '',
                        phone: '',
                        address: {
                          country: 'US'
                        }
                      }
                    },
                    fields: {
                      billingDetails: {
                        email: 'auto',
                        name: 'auto',
                        phone: 'auto',
                        address: 'auto'
                      }
                    },
                    business: {
                      name: 'SnuggleCrafters'
                    }
                  }}
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isProcessing}
                data-testid="button-back"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
                disabled={!stripe || isProcessing}
                data-testid="button-submit-payment"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  "Complete Purchase"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function BuyGiftCardPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-purple-950 dark:via-gray-900 dark:to-teal-950">
      <Navigation />
      <BuyGiftCardForm />
    </div>
  );
}
